import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControlLabel,
  Checkbox,
  IconButton,
  Stack,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  People as PeopleIcon,
  Person as PersonIcon
} from '@mui/icons-material';

const roundTo2 = (num) => Math.round(num * 100) / 100;
const formatCurrency = (amount) => '₱' + parseFloat(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatMonth = (dateString) => new Date(dateString).toLocaleDateString('en-PH', { year: 'numeric', month: 'long' });

const PaymentConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  billCount,
  grandTotal,
  creditBalance = 0,
  loading = false,
  totalSurcharge = 0,
  autoSelectedAmount = null,
  customer = null,
  customers = [],
  customerBreakdowns = [],
  selectedBillsData = [],
  selectedBillIds = [],
  allBillsWithSurcharge = [],
  onPrintReceipt = null
}) => {
  const [waiveSurcharge, setWaiveSurcharge] = useState(false);

  // Normalize customer list and bills data
  const customerList = customers && customers.length > 0 ? customers : (customer ? [customer] : []);
  const isMultiCustomer = customerList.length > 1;

  // Resolve all selected bills with customer context
  const resolvedSelectedBills = (selectedBillsData && selectedBillsData.length > 0)
    ? selectedBillsData
    : (allBillsWithSurcharge || []).filter(bill => (selectedBillIds || []).includes(bill.billid));

  // Build per-customer breakdowns dynamically
  const resolvedCustomerBreakdowns = customerList.map(cust => {
    const existingCb = (customerBreakdowns || []).find(cb => cb.customerid === cust.customerid);
    const custBills = resolvedSelectedBills
      .filter(b => b.customerid === cust.customerid || b.customer?.customerid === cust.customerid)
      .sort((a, b) => new Date(a.billedmonth) - new Date(b.billedmonth));

    const custBasic = roundTo2(custBills.reduce((sum, b) => sum + parseFloat(b.basicamount || 0), 0));
    const custSurchargeRaw = roundTo2(custBills.reduce((sum, b) => sum + parseFloat(b.surchargeInfo?.surchargeAmount || b.surchargeamount || 0), 0));
    const custSurcharge = waiveSurcharge ? 0 : custSurchargeRaw;
    const custDiscount = roundTo2(custBills.reduce((sum, b) => sum + parseFloat(b.calculatedDiscountAmount || b.discountamount || 0), 0));
    const custCreditApplied = roundTo2(custBills.reduce((sum, b) => sum + parseFloat(b.creditApplied || 0), 0));
    
    // Net cash payable for selected bills
    const custNetDue = roundTo2(Math.max(0, custBasic + custSurcharge - custDiscount - custCreditApplied));

    // Auto-selected amount on this customer tab (if any)
    const autoAmount = existingCb?.autoSelectedAmount !== undefined 
      ? existingCb.autoSelectedAmount 
      : (!isMultiCustomer ? autoSelectedAmount : cust.autoSelectedAmount);
    
    const parsedAuto = (autoAmount !== null && autoAmount !== undefined && autoAmount !== '')
      ? roundTo2(parseFloat(autoAmount))
      : null;

    const custExcess = (parsedAuto !== null && parsedAuto > custNetDue) 
      ? roundTo2(parsedAuto - custNetDue) 
      : (existingCb?.excessPayment || 0);
    const custEffectivePayment = (parsedAuto !== null && parsedAuto > custNetDue) 
      ? parsedAuto 
      : (custNetDue + custExcess);

    let monthRange = '';
    if (custBills.length === 1) {
      monthRange = formatMonth(custBills[0].billedmonth);
    } else if (custBills.length > 1) {
      monthRange = `${formatMonth(custBills[0].billedmonth)} - ${formatMonth(custBills[custBills.length - 1].billedmonth)}`;
    }

    return {
      customer: cust,
      customerid: cust.customerid,
      name: cust.name,
      accountno: cust.accountnumber || cust.accountno || cust.oldaccountno || cust.customerid,
      barangay: cust.barangay,
      bills: custBills,
      billsCount: custBills.length,
      monthRange,
      basicAmount: custBasic,
      surchargeAmount: custSurcharge,
      rawSurcharge: custSurchargeRaw,
      discountAmount: custDiscount,
      creditApplied: custCreditApplied,
      creditBalance: parseFloat(cust.credit_balance || 0),
      netDue: custNetDue,
      autoSelectedAmount: autoAmount,
      excessPayment: custExcess,
      effectivePayment: custEffectivePayment
    };
  });

  // Calculate base amounts
  const grandTotalWithoutSurcharge = roundTo2(grandTotal - totalSurcharge);
  const effectiveGrandTotal = roundTo2(waiveSurcharge ? grandTotalWithoutSurcharge : grandTotal);
  const creditToApply = roundTo2(Math.min(creditBalance, effectiveGrandTotal));
  const amountAfterCredit = roundTo2(effectiveGrandTotal - creditToApply);

  const totalEffectiveFromBreakdowns = roundTo2(resolvedCustomerBreakdowns.reduce((sum, cb) => sum + cb.effectivePayment, 0));
  const totalExcessFromBreakdowns = roundTo2(resolvedCustomerBreakdowns.reduce((sum, cb) => sum + cb.excessPayment, 0));

  // If autoSelectedAmount was set in "Auto-Select by Amount" (e.g. ₱500.00):
  const parsedAutoAmount = (autoSelectedAmount !== null && autoSelectedAmount !== undefined && autoSelectedAmount !== '') 
    ? roundTo2(parseFloat(autoSelectedAmount)) 
    : 0;
  const hasAutoSelectedExcess = parsedAutoAmount > amountAfterCredit;
  const effectivePaymentAmount = isMultiCustomer 
    ? totalEffectiveFromBreakdowns 
    : (hasAutoSelectedExcess ? parsedAutoAmount : amountAfterCredit);
  const excessPaymentAmount = isMultiCustomer
    ? totalExcessFromBreakdowns
    : roundTo2(Math.max(0, effectivePaymentAmount - amountAfterCredit));

  useEffect(() => {
    if (isOpen) {
      setWaiveSurcharge(false);
    }
  }, [isOpen]);

  // Compute live Official Receipt (OR) Breakdown
  const computeReceiptBreakdown = () => {
    const effectiveSurcharge = waiveSurcharge ? 0 : totalSurcharge;
    const targetPayment = effectivePaymentAmount;
    const netCashForBasic = roundTo2(Math.max(0, targetPayment - effectiveSurcharge));
    let remainingCash = netCashForBasic;

    const lineItems = [];
    const totalBasicSelected = roundTo2(resolvedSelectedBills.reduce((sum, b) => sum + parseFloat(b.basicamount || 0), 0));
    const totalDiscountSelected = roundTo2(resolvedSelectedBills.reduce((sum, b) => sum + parseFloat(b.calculatedDiscountAmount || b.discountamount || 0), 0));
    const totalCreditAppliedSelected = roundTo2(resolvedSelectedBills.reduce((sum, b) => sum + parseFloat(b.creditApplied || 0), 0));

    if (!isMultiCustomer) {
      // Single Customer Mode
      const allSortedBills = [...(allBillsWithSurcharge && allBillsWithSurcharge.length > 0 ? allBillsWithSurcharge : resolvedSelectedBills)]
        .sort((a, b) => new Date(a.billedmonth) - new Date(b.billedmonth));

      const balanceItems = [];
      const fullMonthBills = [];
      const partialItems = [];
      let unbilledAdvanceCash = 0;

      for (const bill of allSortedBills) {
        const isSelected = (selectedBillIds || []).includes(bill.billid) || resolvedSelectedBills.some(b => b.billid === bill.billid);
        const bBasic = roundTo2(parseFloat(bill.basicamount || 0));
        const bDiscount = roundTo2(parseFloat(bill.calculatedDiscountAmount || 0));
        const bCredit = roundTo2(parseFloat(bill.creditApplied || 0));
        const netBasicDue = roundTo2(Math.max(0, bBasic - bDiscount - bCredit));

        if (isSelected) {
          if (remainingCash >= netBasicDue && netBasicDue > 0) {
            if (bCredit > 0) {
              const deductionParts = [`₱${bCredit.toFixed(2)} credit`];
              if (bDiscount > 0) deductionParts.push(`₱${bDiscount.toFixed(2)} discount`);
              const deductionLabel = deductionParts.join(' & ');
              balanceItems.push({
                label: `Balance of ${formatMonth(bill.billedmonth)}`,
                amount: netBasicDue,
                type: 'balance_basic',
                month: formatMonth(bill.billedmonth),
                deductionNote: `₱${bBasic.toFixed(2)} basic less ${deductionLabel}`,
                deductionApplied: roundTo2(bCredit + bDiscount)
              });
            } else {
              fullMonthBills.push(bill);
            }
            remainingCash = roundTo2(remainingCash - netBasicDue);
          } else if (netBasicDue <= 0 && bCredit > 0) {
            balanceItems.push({
              label: `Balance of ${formatMonth(bill.billedmonth)}`,
              amount: 0,
              type: 'credit_covered',
              month: formatMonth(bill.billedmonth),
              deductionNote: `₱${bBasic.toFixed(2)} basic fully covered by active credit`,
              deductionApplied: bCredit
            });
          }
        } else {
          // Upcoming unselected bill
          if (remainingCash >= netBasicDue && netBasicDue > 0) {
            fullMonthBills.push(bill);
            remainingCash = roundTo2(remainingCash - netBasicDue);
          } else if (remainingCash > 0) {
            partialItems.push({
              label: `Partial Payment of ${formatMonth(bill.billedmonth)}`,
              amount: remainingCash,
              type: 'partial_basic',
              month: formatMonth(bill.billedmonth),
              note: `₱${remainingCash.toFixed(2)} advance payment applied to upcoming bill`
            });
            remainingCash = 0;
            break;
          } else if (bCredit > 0) {
            partialItems.push({
              label: `Partial Credit applied to ${formatMonth(bill.billedmonth)}`,
              amount: bCredit,
              type: 'credit_applied_upcoming',
              month: formatMonth(bill.billedmonth),
              note: `₱${bCredit.toFixed(2)} active credit applied to upcoming bill`
            });
          }
        }
      }

      if (remainingCash > 0) {
        unbilledAdvanceCash = remainingCash;
      }

      // 1. Balance of specific billed months
      balanceItems.forEach(item => {
        lineItems.push({
          lineNo: lineItems.length + 1,
          ...item
        });
      });

      // 2. Months covered in full (COMBINED into single line)
      if (fullMonthBills.length > 0) {
        const totalFullAmount = roundTo2(fullMonthBills.reduce((sum, b) => {
          const basic = parseFloat(b.basicamount || 0);
          const discount = parseFloat(b.calculatedDiscountAmount || b.discountamount || 0);
          return sum + Math.max(0, basic - discount);
        }, 0));
        let combinedLabel = '';
        if (fullMonthBills.length === 1) {
          combinedLabel = `Water Bill (${formatMonth(fullMonthBills[0].billedmonth)})`;
        } else {
          const firstM = formatMonth(fullMonthBills[0].billedmonth);
          const lastM = formatMonth(fullMonthBills[fullMonthBills.length - 1].billedmonth);
          combinedLabel = `Water Bill (${firstM} - ${lastM})`;
        }

        lineItems.push({
          lineNo: lineItems.length + 1,
          label: combinedLabel,
          amount: totalFullAmount,
          type: 'combined_full_basic',
          monthsCount: fullMonthBills.length,
          monthsList: fullMonthBills.map(b => formatMonth(b.billedmonth)).join(', ')
        });
      }

      // 3. Partial payment for specific billed month
      partialItems.forEach(item => {
        lineItems.push({
          lineNo: lineItems.length + 1,
          ...item
        });
      });

      // 4. Advance unbilled cash
      if (unbilledAdvanceCash > 0) {
        const latestBill = allSortedBills[allSortedBills.length - 1];
        let nextMonthLabel = '';
        if (latestBill && latestBill.billedmonth) {
          const nextDate = new Date(latestBill.billedmonth);
          nextDate.setMonth(nextDate.getMonth() + 1);
          nextMonthLabel = formatMonth(nextDate);
        } else {
          const nextDate = new Date();
          nextDate.setMonth(nextDate.getMonth() + 1);
          nextMonthLabel = formatMonth(nextDate);
        }

        lineItems.push({
          lineNo: lineItems.length + 1,
          label: `Partial Payment of ${nextMonthLabel}`,
          amount: unbilledAdvanceCash,
          type: 'partial_basic',
          month: nextMonthLabel,
          note: `₱${unbilledAdvanceCash.toFixed(2)} advance payment applied to upcoming bill`
        });
      }
    } else {
      // Multi-Customer Mode: Group line items by customer
      resolvedCustomerBreakdowns.forEach(cb => {
        const cust = cb.customer;
        const custAllBills = (allBillsWithSurcharge && allBillsWithSurcharge.length > 0)
          ? allBillsWithSurcharge.filter(b => b.customerid === cust.customerid || b.customer?.customerid === cust.customerid)
          : resolvedSelectedBills.filter(b => b.customerid === cust.customerid || b.customer?.customerid === cust.customerid);

        const custSortedBills = [...custAllBills].sort((a, b) => new Date(a.billedmonth) - new Date(b.billedmonth));

        const balanceItems = [];
        const fullMonthBills = [];
        const partialItems = [];

        let custCreditBalance = cb.creditBalance;
        let remainingCustCredit = custCreditBalance;

        // Process selected bills for this customer
        const custSelectedBills = custSortedBills.filter(bill => 
          (selectedBillIds || []).includes(bill.billid) || resolvedSelectedBills.some(b => b.billid === bill.billid)
        );

        if (custSelectedBills.length === 0) return;

        custSelectedBills.forEach(bill => {
          const bBasic = roundTo2(parseFloat(bill.basicamount || 0));
          const bDiscount = roundTo2(parseFloat(bill.calculatedDiscountAmount || bill.discountamount || 0));
          const bCredit = roundTo2(parseFloat(bill.creditApplied || 0));
          const netBasicDue = roundTo2(Math.max(0, bBasic - bDiscount - bCredit));

          if (bCredit > 0) {
            const deductionParts = [`₱${bCredit.toFixed(2)} credit`];
            if (bDiscount > 0) deductionParts.push(`₱${bDiscount.toFixed(2)} discount`);
            const deductionLabel = deductionParts.join(' & ');

            if (netBasicDue > 0) {
              balanceItems.push({
                label: `Balance of ${formatMonth(bill.billedmonth)} — ${cb.name}`,
                amount: netBasicDue,
                type: 'balance_basic',
                customerName: cb.name,
                month: formatMonth(bill.billedmonth),
                deductionNote: `₱${bBasic.toFixed(2)} basic less ${deductionLabel}`,
                deductionApplied: roundTo2(bCredit + bDiscount)
              });
            } else {
              balanceItems.push({
                label: `Balance of ${formatMonth(bill.billedmonth)} — ${cb.name}`,
                amount: 0,
                type: 'credit_covered',
                customerName: cb.name,
                month: formatMonth(bill.billedmonth),
                deductionNote: `₱${bBasic.toFixed(2)} basic fully covered by active advance credit`,
                deductionApplied: bCredit
              });
            }
            remainingCustCredit = roundTo2(Math.max(0, remainingCustCredit - bCredit));
          } else {
            fullMonthBills.push(bill);
          }
        });

        // Add balance items for this customer
        balanceItems.forEach(item => {
          lineItems.push({
            lineNo: lineItems.length + 1,
            ...item
          });
        });

        // Add combined full month bills for this customer
        if (fullMonthBills.length > 0) {
          const totalFullAmount = roundTo2(fullMonthBills.reduce((sum, b) => {
            const basic = parseFloat(b.basicamount || 0);
            const discount = parseFloat(b.calculatedDiscountAmount || b.discountamount || 0);
            return sum + Math.max(0, basic - discount);
          }, 0));
          let monthRange = '';
          if (fullMonthBills.length === 1) {
            monthRange = formatMonth(fullMonthBills[0].billedmonth);
          } else {
            const firstM = formatMonth(fullMonthBills[0].billedmonth);
            const lastM = formatMonth(fullMonthBills[fullMonthBills.length - 1].billedmonth);
            monthRange = `${firstM} - ${lastM}`;
          }

          lineItems.push({
            lineNo: lineItems.length + 1,
            label: `Water Bill — ${cb.name} (${monthRange})`,
            amount: totalFullAmount,
            type: 'combined_full_basic',
            customerName: cb.name,
            monthsCount: fullMonthBills.length
          });
        }

        // Check if customer has remaining advance credit to itemize against upcoming months
        if (remainingCustCredit > 0) {
          const custUnselectedBills = custSortedBills.filter(bill => 
            !(selectedBillIds || []).includes(bill.billid) && !resolvedSelectedBills.some(b => b.billid === bill.billid)
          );

          for (const bill of custUnselectedBills) {
            const bBasic = roundTo2(parseFloat(bill.basicamount || 0));
            const bDiscount = roundTo2(parseFloat(bill.calculatedDiscountAmount || bill.discountamount || 0));
            const netBasic = roundTo2(Math.max(0, bBasic - bDiscount));

            if (remainingCustCredit >= netBasic && netBasic > 0) {
              partialItems.push({
                label: `Partial Payment of ${formatMonth(bill.billedmonth)} — ${cb.name}`,
                amount: 0,
                type: 'credit_applied_upcoming',
                customerName: cb.name,
                month: formatMonth(bill.billedmonth),
                note: `₱${netBasic.toFixed(2)} active advance credit fully covers upcoming bill`
              });
              remainingCustCredit = roundTo2(remainingCustCredit - netBasic);
            } else if (remainingCustCredit > 0) {
              partialItems.push({
                label: `Partial Payment of ${formatMonth(bill.billedmonth)} — ${cb.name}`,
                amount: 0,
                type: 'credit_applied_upcoming',
                customerName: cb.name,
                month: formatMonth(bill.billedmonth),
                note: `₱${remainingCustCredit.toFixed(2)} active advance credit applied to upcoming bill`
              });
              remainingCustCredit = 0;
              break;
            }
          }

          if (remainingCustCredit > 0) {
            const latestBill = custSortedBills[custSortedBills.length - 1];
            let nextMonthLabel = '';
            if (latestBill && latestBill.billedmonth) {
              const nextDate = new Date(latestBill.billedmonth);
              nextDate.setMonth(nextDate.getMonth() + 1);
              nextMonthLabel = formatMonth(nextDate);
            } else {
              const nextDate = new Date();
              nextDate.setMonth(nextDate.getMonth() + 1);
              nextMonthLabel = formatMonth(nextDate);
            }

            partialItems.push({
              label: `Partial Payment of ${nextMonthLabel} — ${cb.name}`,
              amount: 0,
              type: 'credit_applied_upcoming',
              customerName: cb.name,
              month: nextMonthLabel,
              note: `₱${remainingCustCredit.toFixed(2)} active advance credit on account for upcoming month`
            });
          }

          partialItems.forEach(item => {
            lineItems.push({
              lineNo: lineItems.length + 1,
              ...item
            });
          });
        }

        // Excess payment from cash tendered / Auto-Select on this customer's account
        if (cb.excessPayment > 0) {
          const latestBill = custSortedBills[custSortedBills.length - 1];
          let nextMonthLabel = '';
          if (latestBill && latestBill.billedmonth) {
            const nextDate = new Date(latestBill.billedmonth);
            nextDate.setMonth(nextDate.getMonth() + 1);
            nextMonthLabel = formatMonth(nextDate);
          } else {
            const nextDate = new Date();
            nextDate.setMonth(nextDate.getMonth() + 1);
            nextMonthLabel = formatMonth(nextDate);
          }

          lineItems.push({
            lineNo: lineItems.length + 1,
            label: `Partial Payment of ${nextMonthLabel} — ${cb.name}`,
            amount: cb.excessPayment,
            type: 'partial_basic',
            customerName: cb.name,
            month: nextMonthLabel,
            note: `₱${cb.excessPayment.toFixed(2)} advance payment applied to upcoming bill`
          });
        }
      });
    }

    // Surcharge line
    if (effectiveSurcharge > 0) {
      lineItems.push({
        lineNo: lineItems.length + 1,
        label: isMultiCustomer ? 'Total Surcharges (All Combined Accounts)' : 'Surcharge',
        amount: effectiveSurcharge,
        type: 'surcharge'
      });
    }

    const totalReceiptAmount = roundTo2(lineItems.reduce((sum, item) => sum + item.amount, 0));

    return {
      selectedBillsCount: resolvedSelectedBills.length,
      customersCount: customerList.length,
      customerBreakdowns: resolvedCustomerBreakdowns,
      totalBasic: totalBasicSelected,
      totalSurcharge: effectiveSurcharge,
      totalDiscount: totalDiscountSelected,
      totalCreditApplied: totalCreditAppliedSelected,
      netCashForBasic,
      totalReceiptAmount,
      lineItems
    };
  };

  const receiptBreakdown = computeReceiptBreakdown();

  const handleConfirm = () => {
    onConfirm(effectivePaymentAmount, creditToApply, excessPaymentAmount, waiveSurcharge);
  };

  const handlePrintSlip = () => {
    if (onPrintReceipt) {
      onPrintReceipt(receiptBreakdown, isMultiCustomer ? customerList : customer, resolvedCustomerBreakdowns);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={loading ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3.5,
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)'
        }
      }}
    >
      {/* Dialog Header */}
      <DialogTitle sx={{ bgcolor: '#0f172a', color: '#ffffff', py: 2, px: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PaymentIcon sx={{ color: '#10b981', fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ color: '#ffffff', lineHeight: 1.2 }}>
              {isMultiCustomer ? 'Combined Multi-Account Payment & Official Receipt' : 'Process Payment & Official Receipt (OR)'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              Confirm payment processing and review details to be written on Official Receipt (Form 25-A)
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={loading} sx={{ color: '#94a3b8', '&:hover': { color: '#ffffff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, bgcolor: '#f8fafc' }}>
        {/* Customer & Bill Overview Card */}
        {!isMultiCustomer && customer && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2.5, bgcolor: '#ffffff', borderRadius: 2.5, borderColor: '#e2e8f0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">CONSUMER / PAYOR</Typography>
                <Typography variant="subtitle1" fontWeight={800} color="#0f172a">{customer.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Account: #{customer.accountnumber || customer.customerid} &bull; Meter: {customer.meternumber || 'N/A'} &bull; {customer.barangay}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} sx={{ textAlign: { sm: 'right' } }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">AMOUNT PAYABLE</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, gap: 1, mt: 0.5 }}>
                  <Chip label={`${billCount} Bill${billCount > 1 ? 's' : ''} Selected`} size="small" sx={{ bgcolor: '#ecfdf5', color: '#047857', fontWeight: 700 }} />
                  <Typography variant="h6" fontWeight={800} color="#059669">
                    {formatCurrency(effectivePaymentAmount)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Multi-Customer Overview Card & Per-Customer Payment Breakdown */}
        {isMultiCustomer && (
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, bgcolor: '#ffffff', borderRadius: 2.5, borderColor: '#0284c7' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <PeopleIcon sx={{ color: '#0284c7', fontSize: 24 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={800} color="#0f172a" sx={{ lineHeight: 1.2 }}>
                    Combined Accounts ({resolvedCustomerBreakdowns.length} Customer Accounts)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {resolvedSelectedBills.length} Total Bills Selected across multiple accounts
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
                  COMBINED TOTAL PAYABLE
                </Typography>
                <Typography variant="h6" fontWeight={800} color="#059669" sx={{ lineHeight: 1.2 }}>
                  {formatCurrency(effectivePaymentAmount)}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 1.5 }} />

            {/* Per-Customer Account Cards */}
            <Stack spacing={1.25}>
              {resolvedCustomerBreakdowns.map((cb) => (
                <Paper
                  key={cb.customerid}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    px: 2,
                    borderRadius: 2,
                    borderColor: cb.excessPayment > 0 ? '#bbf7d0' : '#e2e8f0',
                    bgcolor: cb.excessPayment > 0 ? '#f0fdf4' : '#f8fafc',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 1.5
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
                        {cb.name}
                      </Typography>
                      <Chip
                        label={`#${cb.accountno}`}
                        size="small"
                        sx={{ bgcolor: '#e2e8f0', color: '#475569', fontWeight: 600, height: 20, fontSize: '0.72rem' }}
                      />
                      {cb.barangay && (
                        <Chip
                          label={cb.barangay}
                          size="small"
                          sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 600, height: 20, fontSize: '0.72rem' }}
                        />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      <strong>{cb.billsCount} bill{cb.billsCount > 1 ? 's' : ''}</strong> ({cb.monthRange}) &bull; Basic: {formatCurrency(cb.basicAmount)}
                      {cb.rawSurcharge > 0 && ` + Surch: ${formatCurrency(cb.surchargeAmount)}`}
                      {cb.discountAmount > 0 && ` - Disc: ${formatCurrency(cb.discountAmount)}`}
                      {cb.creditApplied > 0 && ` - Credit: ${formatCurrency(cb.creditApplied)}`}
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
                      AMOUNT TO BE PAID
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={800} color="#059669">
                      {formatCurrency(cb.effectivePayment)}
                    </Typography>
                    {cb.excessPayment > 0 && (
                      <Chip
                        label={`incl. ${formatCurrency(cb.excessPayment)} advance`}
                        size="small"
                        sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700, height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Paper>
        )}

        {/* Waive Surcharges Control */}
        {totalSurcharge > 0 && (
          <Paper variant="outlined" sx={{ p: 1.75, px: 2.5, mb: 2.5, bgcolor: '#ffffff', borderRadius: 2.5, borderColor: waiveSurcharge ? '#fca5a5' : '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={waiveSurcharge}
                  onChange={(e) => setWaiveSurcharge(e.target.checked)}
                  disabled={loading}
                  sx={{ color: '#dc2626', '&.Mui-checked': { color: '#dc2626' } }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 700, color: waiveSurcharge ? '#dc2626' : '#334155' }}>
                  Waive Surcharges (<strong>-{formatCurrency(totalSurcharge)}</strong>)
                </Typography>
              }
            />
            {waiveSurcharge && (
              <Chip label="Surcharges Waived" size="small" sx={{ bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 700 }} />
            )}
          </Paper>
        )}

        {/* Official Receipt (OR) Breakdown Table */}
        <Typography variant="subtitle2" fontWeight={800} color="#0f172a" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptIcon sx={{ color: '#059669', fontSize: 20 }} />
          Particulars & Amounts to be Written on Official Receipt (Form 25-A):
        </Typography>

        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2.5, borderRadius: 2.5, borderColor: '#e2e8f0', bgcolor: '#ffffff' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f1f5f9' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: 50, color: '#475569' }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Nature of Collection / Particulars</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569' }} align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {receiptBreakdown.lineItems.map((item, index) => (
                <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell sx={{ color: '#64748b', fontWeight: 600 }}>{index + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} color="#0f172a">
                      {item.label}
                    </Typography>
                    {item.deductionNote && (
                      <Typography variant="caption" color="#059669" display="block">
                        {item.deductionNote}
                      </Typography>
                    )}
                    {item.note && (
                      <Typography variant="caption" color="#0284c7" display="block">
                        {item.note}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: '0.95rem', color: '#0f172a' }}>
                    {formatCurrency(item.amount)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ bgcolor: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                <TableCell colSpan={2} sx={{ fontWeight: 800, color: '#0f172a', py: 1.5 }}>
                  TOTAL AMOUNT TO BE WRITTEN ON OFFICIAL RECEIPT
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 900, color: '#059669', fontSize: '1.15rem', fontFamily: 'monospace', py: 1.5 }}>
                  {formatCurrency(receiptBreakdown.totalReceiptAmount)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      {/* Dialog Footer Actions */}
      <DialogActions sx={{ p: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          sx={{ borderRadius: 2, color: '#64748b', borderColor: '#cbd5e1', textTransform: 'none', px: 2.5 }}
        >
          Cancel
        </Button>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {onPrintReceipt && (
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrintSlip}
              disabled={loading}
              sx={{ borderRadius: 2, borderColor: '#0284c7', color: '#0284c7', textTransform: 'none', px: 2, '&:hover': { bgcolor: '#f0f9ff' } }}
            >
              Print Receipt Details Slip
            </Button>
          )}

          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={loading}
            sx={{
              borderRadius: 2,
              bgcolor: '#059669',
              color: '#ffffff',
              fontWeight: 800,
              px: 3.5,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)',
              '&:hover': { bgcolor: '#047857' }
            }}
          >
            {loading ? 'Processing Payment...' : `Confirm & Process Payment (${formatCurrency(effectivePaymentAmount)})`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentConfirmDialog;
